import { useEffect, useState } from 'react';
import { PlaylistService } from '@/services/playlist.service';
import { PlaylistCard } from '@/components/playlists/PlaylistCard';
import { CreatePlaylistModal } from '@/components/playlists/CreatePlaylistModal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import type { Playlist } from '@/types';

export function PlaylistsPage() {
    const { user } = useAuth();
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const fetchPlaylists = async () => {
        if (!user) return;
        try {
            setIsLoading(true);
            const data = await PlaylistService.getUserPlaylists(user.id);
            setPlaylists(data);
        } catch (error) {
            console.error('Failed to load playlists', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPlaylists();
    }, [user]);

    const handlePlaylistCreated = (_newId: string) => {
        fetchPlaylists(); // Refresh list
        // Optionally navigate to new playlist: navigate(`/playlists/${newId}`)
    };

    if (isLoading && playlists.length === 0) {
        return <LoadingSpinner fullScreen />;
    }

    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="h3 m-0">My Playlists</h1>
                <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                    <i className="bi bi-plus-lg me-2"></i>
                    Create Playlist
                </button>
            </div>

            {playlists.length === 0 ? (
                <div className="text-center py-5">
                    <div className="mb-3">
                        <i className="bi bi-music-note-list text-muted display-1 opacity-25"></i>
                    </div>
                    <h3 className="h5 text-muted">No Playlists Yet</h3>
                    <p className="text-muted mb-4">Create your first playlist to get started.</p>
                    <button className="btn btn-outline-primary" onClick={() => setShowCreateModal(true)}>
                        Create Playlist
                    </button>
                </div>
            ) : (
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4">
                    {playlists.map(playlist => (
                        <div className="col" key={playlist.id}>
                            <PlaylistCard playlist={playlist} />
                        </div>
                    ))}
                </div>
            )}

            <CreatePlaylistModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onCreated={handlePlaylistCreated}
            />
        </div>
    );
}
